/**
 * SG AGM preset forms — 챗봇 member / contact lookup 공통.
 */
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';

export interface AgmMemberPersonFormSlice {
  memberName: string;
  memberRepresentativeName: string;
  members: Array<{ name: string; contactCd?: string }>;
}

export type AgmSoleMemberType = 'sole_corporate' | 'sole_individual';
export type SgMemberEntityKind = 'corporate' | 'individual';

/** SG members API row → 법인 주주 여부 (AgmUfsForm autofill 과 동일 기준) */
export function isSgCorporateShareholder(raw: Record<string, unknown>): boolean {
  const categoryCd = String(raw.categoryCd || raw.CATEGORY_CD || '').trim().toUpperCase();
  if (categoryCd === 'B09001') return true;
  if (categoryCd === 'B09002') return false;
  return raw.entityType === 'COMPANY'
    || raw.idType === 'UEN'
    || raw.type === 'corporate';
}

export function filterSgShareholdersByEntityKind<T extends Record<string, unknown>>(
  rows: T[],
  kind: SgMemberEntityKind,
): T[] {
  if (kind === 'corporate') {
    return rows.filter((row) => isSgCorporateShareholder(row));
  }
  return rows.filter((row) => !isSgCorporateShareholder(row));
}

export function buildAgmSoleMemberNameLookupContext(
  memberType: AgmSoleMemberType,
): Record<string, unknown> {
  return {
    fieldGroup: 'soleMember',
    defaultRole: 'SHAREHOLDER',
    memberEntityKind: memberType === 'sole_corporate' ? 'corporate' : 'individual',
  };
}

export function buildAgmMemberRepresentativeLookupContext(): Record<string, unknown> {
  return { fieldGroup: 'memberRepresentative', defaultRole: 'CONTACT' };
}

export function buildAgmMemberRowLookupContext(index: number): Record<string, unknown> {
  return { fieldGroup: 'member', index, defaultRole: 'SHAREHOLDER' };
}

export function resolveAgmMemberDisplayName(person: OfficerDetail): string {
  const raw = person as unknown as Record<string, unknown>;
  return String(
    person.name
    || person.contactName
    || `${raw.firstName || ''} ${raw.surname || raw.lastName || ''}`.trim(),
  ).trim();
}

/** selectedPerson + activePersonContext → member 관련 필드 채움 */
export function applyAgmMemberPersonSelection(
  model: AgmMemberPersonFormSlice,
  ctx: Record<string, unknown>,
  person: OfficerDetail,
): boolean {
  const name = resolveAgmMemberDisplayName(person);
  if (!name) return false;

  const fg = String(ctx.fieldGroup || '');
  if (fg === 'soleMember') {
    model.memberName = name;
    return true;
  }
  if (fg === 'memberRepresentative') {
    model.memberRepresentativeName = name;
    return true;
  }
  if (fg === 'member' && typeof ctx.index === 'number') {
    const row = model.members[ctx.index];
    if (!row) return false;
    row.name = name;
    const cd = String(person.contactCd || '').trim();
    if (cd) row.contactCd = cd;
    return true;
  }
  return false;
}
